import React, { useEffect, useRef } from "react";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * Widget de Cloudflare Turnstile.
 * Solo se renderiza si hay REACT_APP_TURNSTILE_SITE_KEY configurada.
 *
 * Props:
 *  - onVerify(token): se llama cuando el usuario supera el reto.
 *  - onExpire(): el token caducó; hay que rehacer el reto.
 *  - resetKey: cambia este valor para forzar un reset del widget (p.ej. tras enviar).
 */
const loadScript = () =>
  new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });

const Turnstile = ({ onVerify, onExpire, resetKey }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerify && onVerify(token),
          "expired-callback": () => onExpire && onExpire(),
          "error-callback": () => onExpire && onExpire(),
        });
      })
      .catch(() => {
        /* si el script falla al cargar, el formulario sigue usando el honeypot */
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  // Reset explícito cuando cambia resetKey (evita reset en el primer render).
  const firstResetRun = useRef(true);
  useEffect(() => {
    if (firstResetRun.current) {
      firstResetRun.current = false;
      return;
    }
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [resetKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} style={{ marginBottom: "16px" }} />;
};

export default Turnstile;
